import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Clock, Timer, Hourglass } from 'lucide-react';
import { ResponseTimeMetrics as ResponseTimeData } from '@/hooks/useDashboardMetrics';

interface ResponseTimeMetricsProps {
  data: ResponseTimeData | undefined;
  isLoading: boolean;
}

const ResponseTimeMetrics = ({ data, isLoading }: ResponseTimeMetricsProps) => {
  if (isLoading) {
    return (
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-blue-600" />
            Tempos de Resposta
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="text-center animate-pulse">
                <div className="h-8 bg-gray-200 rounded mb-2"></div>
                <div className="h-4 bg-gray-200 rounded mb-2"></div>
                <div className="h-2 bg-gray-200 rounded"></div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const getProgressValue = (value: number, max: number) => {
    return Math.min((value / max) * 100, 100);
  };

  const getProgressColor = (value: number, target: number) => {
    if (value <= target) return 'bg-green-500';
    if (value <= target * 1.2) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <Card className="mb-6 hover:shadow-lg transition-all duration-300">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-blue-600">
          <Clock className="h-5 w-5" />
          Tempos de Resposta
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Primeira Resposta */}
          <div className="text-center p-4 rounded-lg bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200">
            <div className="flex items-center justify-center mb-3">
              <div className="p-2 bg-blue-500 rounded-full">
                <Timer className="h-5 w-5 text-white" />
              </div>
            </div>
            <div className="text-2xl font-bold text-blue-600 mb-1">
              {data.firstResponse}h
            </div>
            <p className="text-sm text-gray-600 mb-3">Primeira Resposta</p>
            <Progress 
              value={getProgressValue(data.firstResponse, 4)} 
              className="h-2"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>Meta: 2h</span>
              <span className={data.firstResponse <= 2 ? 'text-green-600' : 'text-red-600'}>
                {data.firstResponse <= 2 ? '✓ No prazo' : '⚠ Atrasado'}
              </span>
            </div>
          </div>

          {/* Resolução Média */}
          <div className="text-center p-4 rounded-lg bg-gradient-to-br from-green-50 to-green-100 border border-green-200">
            <div className="flex items-center justify-center mb-3">
              <div className="p-2 bg-green-500 rounded-full">
                <Clock className="h-5 w-5 text-white" />
              </div>
            </div>
            <div className="text-2xl font-bold text-green-600 mb-1">
              {data.averageResolution}h
            </div>
            <p className="text-sm text-gray-600 mb-3">Resolução Média</p>
            <Progress 
              value={getProgressValue(data.averageResolution, 8)} 
              className="h-2"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>Meta: 4h</span>
              <span className={data.averageResolution <= 4 ? 'text-green-600' : 'text-red-600'}>
                {data.averageResolution <= 4 ? '✓ No prazo' : '⚠ Atrasado'}
              </span>
            </div>
          </div>

          {/* Tempo de Espera */}
          <div className="text-center p-4 rounded-lg bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200">
            <div className="flex items-center justify-center mb-3">
              <div className="p-2 bg-orange-500 rounded-full">
                <Hourglass className="h-5 w-5 text-white" />
              </div>
            </div>
            <div className="text-2xl font-bold text-orange-600 mb-1">
              {data.averageWaitTime}h
            </div>
            <p className="text-sm text-gray-600 mb-3">Tempo de Espera</p>
            <Progress 
              value={getProgressValue(data.averageWaitTime, 3)} 
              className="h-2"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>Meta: 1h</span>
              <span className={data.averageWaitTime <= 1 ? 'text-green-600' : 'text-red-600'}>
                {data.averageWaitTime <= 1 ? '✓ No prazo' : '⚠ Atrasado'}
              </span>
            </div>
          </div>
        </div>

        {/* Resumo Geral */}
        <div className="mt-6 p-4 bg-gray-50 rounded-lg border">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-semibold text-gray-800">Performance Geral</h4>
              <p className="text-sm text-gray-600">Baseado nos últimos 30 dias</p>
            </div>
            <div className="text-right">
              <div className="text-lg font-bold text-blue-600">
                {Math.round(((4 - data.firstResponse) / 4 + (8 - data.averageResolution) / 8 + (3 - data.averageWaitTime) / 3) / 3 * 100)}%
              </div>
              <p className="text-xs text-gray-500">Eficiência</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ResponseTimeMetrics;
